// ============================================================
// A folder in the old Workshop layout, read through a plain directory INPUT.
//
// The session's browser only ever READS - it imports characters; authoring
// is the Assets page's since the folder Workshop retired (assets stage 4). `showDirectoryPicker` buys write
// access nobody here wants, and charges for it: it needs a SECURE
// CONTEXT, so over a dev server reached across a tailnet it does not
// exist at all, and it is Chromium-only besides. The folder browser
// was therefore invisible to the one person using it (v8.23.1).
//
// `<input type="file" webkitdirectory>` hands over every file under a
// chosen directory, with its relative path, in every browser and on
// plain HTTP. For reading a tree that is the whole job.
//
// THE LAYOUT IS THE WORKSHOP'S, because that is what it is reading:
//   root/<Character>/<iteration>.json
//   root/<Group>/<Character>/<iteration>.json
// and a loose `<Character>.json` at the root, which the Workshop's own
// export produces.
// ============================================================

export interface FileCharacter {
  /** The character's folder name - or the file's, for a loose JSON. */
  name: string;
  /** Which group it sits in, or null at the root. */
  group: string | null;
  /** The LATEST iteration; a character folder is a timeline. */
  file: File;
  /** Every iteration, oldest first. Import a folder on the Assets page keeps them all as versions. */
  iterations: File[];
}

export interface FileTree {
  rootCharacters: FileCharacter[];
  groups: { name: string; characters: FileCharacter[] }[];
}

/** The path a directory input reports, minus the folder you picked. */
function relativeParts(file: File): string[] {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  const parts = rel.split('/').filter(Boolean);
  // The first segment is the chosen directory itself, which is not
  // structure - picking `characters/` and picking `foo/` must read the
  // same. A file with no path at all keeps its single segment.
  return parts.length > 1 ? parts.slice(1) : parts;
}

const isJson = (name: string) => name.toLowerCase().endsWith('.json');

/**
 * Group a flat FileList into the tree the shelf draws.
 *
 * Anything that is not JSON is ignored rather than reported: a
 * characters folder in real life has a `.DS_Store` and a stray note in
 * it, and neither is a failure worth telling somebody about.
 */
type Folder = { name: string; group: string | null; files: File[] };

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

/** Where a file sits: its character folder's name, and the group holding it if any. */
function placeOf(parts: string[]): { group: string | null; name: string } {
  // group / character / iteration.json - deeper nestings collapse to
  // their last two folders, which is the only reading that keeps a
  // character whose folder sits three deep.
  if (parts.length >= 3) return { group: parts[parts.length - 3], name: parts[parts.length - 2] };
  if (parts.length === 2) return { group: null, name: parts[0] };
  // A loose file at the root IS the character, named for the file.
  return { group: null, name: parts[0].replace(/\.json$/i, '') };
}

/** The JSON files, gathered by the character folder they sit in. */
function foldersOf(files: File[]): Folder[] {
  const byKey = new Map<string, Folder>();
  for (const file of files) {
    if (!isJson(file.name)) continue;
    const { group, name } = placeOf(relativeParts(file));
    const key = `${group ?? ''}/${name}`;
    const entry = byKey.get(key) ?? { name, group, files: [] };
    entry.files.push(file);
    byKey.set(key, entry);
  }
  return [...byKey.values()];
}

/**
 * THE LATEST ITERATION IS THE CHARACTER. `001, 002, 007-taller-walls`
 * sorts by name, which is how the Workshop reads the same folder.
 */
function characterOf(entry: Folder): FileCharacter {
  const iterations = [...entry.files].sort((a, b) => a.name.localeCompare(b.name));
  return { name: entry.name, group: entry.group, file: iterations[iterations.length - 1], iterations };
}

export function treeFromFileList(files: File[]): FileTree {
  const rootCharacters: FileCharacter[] = [];
  const groups = new Map<string, FileCharacter[]>();
  for (const entry of foldersOf(files)) {
    const character = characterOf(entry);
    if (entry.group === null) rootCharacters.push(character);
    else groups.set(entry.group, [...(groups.get(entry.group) ?? []), character]);
  }
  return {
    rootCharacters: rootCharacters.sort(byName),
    groups: [...groups.entries()]
      .map(([name, characters]) => ({ name, characters: characters.sort(byName) }))
      .sort(byName),
  };
}

/** Every character in the tree, groups and loose ones alike. */
export function allFileCharacters(tree: FileTree): FileCharacter[] {
  return [...tree.groups.flatMap((g) => g.characters), ...tree.rootCharacters];
}

/** The ones whose path is in `paths` - `Group/Name`, or `Name` at the root. */
export function fileCharactersAt(tree: FileTree, paths: Set<string>): FileCharacter[] {
  return allFileCharacters(tree).filter((c) => paths.has(c.group ? `${c.group}/${c.name}` : c.name));
}

/**
 * The iteration a character should be read at, for a named build.
 *
 * A character folder is a timeline - `001.json`, `004-mission3.json`,
 * `007-mission4.json` - and "the latest of everyone" is the wrong answer
 * the moment a campaign has history. Each mission wants a different
 * version of each character, and picking dozens of dropdowns every time
 * is not a workflow (v8.25.0).
 *
 * THE LABEL IS ALREADY IN THE FILENAME. Nothing new to author, no
 * manifest to keep in step, no second file format: type `mission-4` and
 * every character contributes the iteration carrying it.
 *
 * FALLING BACK IS NORMAL, NOT AN ERROR. A character who did not change
 * for that mission has no file naming it, and their latest IS their
 * mission-4 build. The caller is told which fell back so the fact is
 * visible rather than silent.
 *
 * SEPARATORS ARE IGNORED ENTIRELY, not merely collapsed: `mission-4`,
 * `mission_4`, `Mission 4` and `mission4` are one label written by one
 * person on four different days, and a file named `007-mission4.json`
 * must answer to all of them. Collapsing them to a single `-` instead
 * left `mission-4` failing to find `mission4` - and the test that
 * spotted it only did so because the fallback happened to return the
 * right file for the wrong reason.
 */
function normaliseLabel(text: string): string {
  return text.toLowerCase().replace(/[\s_-]+/g, '');
}

export interface LabelPick {
  character: FileCharacter;
  /** The file chosen - the labelled one, or the latest. */
  file: File;
  matched: boolean;
}

export function pickByLabel(characters: FileCharacter[], label: string): LabelPick[] {
  const wanted = normaliseLabel(label);
  return characters.map((character) => {
    if (!wanted) return { character, file: character.file, matched: false };
    // LATEST FIRST among the matches: a character may carry the same
    // label twice (a fix made after the mission was first saved), and
    // the later one is the one that was played.
    const hit = [...character.iterations].reverse().find((f) => normaliseLabel(f.name).includes(wanted));
    return { character, file: hit ?? character.file, matched: !!hit };
  });
}

/**
 * A file name's label: `007-taller-walls.json` → `taller-walls`. A bare
 * `001.json` has no label and contributes nothing.
 */
function labelOf(fileName: string): string | null {
  const m = fileName.replace(/\.json$/i, '').match(/^\d+[-_\s]+(.+)$/);
  return m ? m[1].toLowerCase() : null;
}

/** Every label the folder mentions, for offering rather than guessing. */
export function labelsIn(characters: FileCharacter[]): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const raw of characters.flatMap((c) => c.iterations.map((f) => labelOf(f.name)))) {
    // The label AS WRITTEN, deduped by how it matches - so the list
    // offers `taller-walls` rather than `tallerwalls`.
    if (raw === null || seen.has(normaliseLabel(raw))) continue;
    seen.add(normaliseLabel(raw));
    labels.push(raw);
  }
  return labels.sort();
}

// ============================================================
// The tree, as `CharacterShelf` wants it.
//
// Lives here rather than in either view because BOTH views need it:
// characters and maps have the same folder shape, so they render the
// same shelf from the same adapter. Duplicating ten lines would have
// been the moment the two quietly diverged.
//
// NO CAST. This used to fabricate a `CharacterDirEntry` with a null
// handle and tell the compiler it was real; the shelf is generic over
// its entry now, so a session says what it actually has (v8.25.0).
// ============================================================

interface FileShelfEntry {
  name: string;
  files: { fileName: string }[];
  /** The folder this row stands for, kept so the actions can read it. */
  entry: FileCharacter;
}

export interface FileShelfTree {
  rootCharacters: FileShelfEntry[];
  groups: { name: string; characters: FileShelfEntry[] }[];
}

export function asShelfTree(tree: FileTree): FileShelfTree {
  const one = (c: FileCharacter): FileShelfEntry => ({
    name: c.name,
    files: c.iterations.map((f) => ({ fileName: f.name })),
    entry: c,
  });
  return {
    rootCharacters: tree.rootCharacters.map(one),
    groups: tree.groups.map((g) => ({ name: g.name, characters: g.characters.map(one) })),
  };
}
